import React, { useEffect, useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import {Select} from "antd";
import { useLazySearchEmployeesQuery } from "../../services/employee.service";
import { useSaveLeavesMutation, useUpdateLeaveMutation } from "../../services/hr.service";
import { useForm, Controller } from "react-hook-form";
import { ClipLoader } from "react-spinners";
import { customStyles } from "../../utils";
import alertify from "alertifyjs";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { LeaveSchema } from "../../utils/validationSchema";
import dayjs from "dayjs";

const { Option } = Select;


export const AdminLeaveAddModelPopup = ({leaveTypes, item}) => {
  const {user} = useSelector((state) => state.user);

  const branch_id = (!['HR Admin', 'Admin', 'HR Assistant'].includes(user?.role)) ? user?.branch?.id : ''
  const [employees, setEmployees] = useState([]);
  const [searchEmployees, {data: searchData, error: searchError}] = useLazySearchEmployeesQuery();
  const [saveLeave, {data: saveData, isLoading: isSaving, isSuccess: saveSuccess, error: saveError}] = useSaveLeavesMutation();
  const [updateLeave, {data: updateData, isLoading: isUpdating, isSuccess: updateSuccess, error: updateError}] = useUpdateLeaveMutation();
  const [noOfDays, setNoOfDays] = useState('');

  const data = saveData || updateData;
  const isLoading = isSaving || isUpdating;
  const isSuccess = saveSuccess || updateSuccess;
  const error = saveError || updateError;
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(LeaveSchema),
  });

  // Reset form when modal is closed
  const resetForm = useCallback(() => {
    reset();
    setEmployees([]);
    setNoOfDays('');
    setStartDate('');
    setEndDate('');
  }, [reset]);

  // Listen for modal close events
  useEffect(() => {
    const modalElement = document.getElementById('leave_modal');
    if (modalElement) {
      const handleModalHide = () => {
        resetForm();
      };

      modalElement.addEventListener('hidden.bs.modal', handleModalHide);

      return () => {
        modalElement.removeEventListener('hidden.bs.modal', handleModalHide);
      };
    }
  }, [resetForm]);

  useEffect(() => {
    if (searchData) {
      setEmployees(searchData?.data)
    }
  }, [searchData, searchError])

  useEffect(() => {
    if (isSuccess && data?.success){
      alertify.success(data?.message);
      document.getElementById('close-modal').click();
      // Form will be reset by the modal close event listener
    }

    if (data && !data.success)
      alertify.error(data.errors);

    if (error)
      alertify.error(error?.message);

  }, [data, error, isSuccess]);

  useEffect(() => {
    if (startDate !== '' && endDate !== '') {
      const date1 = dayjs(endDate)
      setNoOfDays(date1.diff(startDate, 'days'))
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (item) {
      setEmployees([...employees, {
        id: item.employee.id,
        name: item.employee.first_name + ' ' + item.employee.last_name,
        designation: item.employee.designation,
        department: item.department
      }]);
      setValue('leave_type_id', item.leave_type.id)
      setValue('employee_id', item.employee.id);
      setValue('reason', item.reason);

      const startDateValue = dayjs(item.start_date).toDate();
      const endDateValue = dayjs(item.end_date).toDate();

      setValue('start_date', startDateValue);
      setValue('end_date', endDateValue);
      setValue('id', item.id);

      // Set the dates for calculation
      setStartDate(startDateValue);
      setEndDate(endDateValue);

      // Calculate and set number of days
      setNoOfDays(item.number_of_days);
    }
  }, [item]);

  const findEmployees = (value) => {
    if (value) {
      searchEmployees({q:value, branch_id});
    } else {
      setEmployees([]);
    }
  }

  const save = payload => {
    const leaveData = {
      employee_id: payload.employee_id,
      leave_type_id: payload.leave_type_id,
      start_date: dayjs(payload.start_date).format('YYYY-MM-DD'),
      end_date: dayjs(payload.end_date).format('YYYY-MM-DD'),
      number_of_days: parseFloat(noOfDays) || 1,
      reason: payload.reason,
    };

    // If editing, use update mutation
    if (payload.id) {
      updateLeave({ id: payload.id, data: leaveData });
    } else {
      saveLeave(leaveData);
    }
  }

  return (
    <>
      <div id="leave_modal" className="modal custom-modal fade" role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{item ? 'Edit' : 'Add'} Leave</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                id="close-modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit(save)}>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Employee <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="employee_id"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        showSearch
                        size="large"
                        value={value}
                        style={{width: '100%'}}
                        defaultActiveFirstOption={false}
                        filterOption={false}
                        onSearch={findEmployees}
                        onChange={onChange}
                        notFoundContent={null}
                      >
                        {employees.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                      </Select>
                    )}
                  />
                  <span className="text-danger">
                      {errors?.employee_id?.message}
                  </span>
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Leave Type <span className="text-danger">*</span>
                  </label>
                  <div className="add-group-btn">
                    <Controller
                      name="leave_type_id"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          size="large"
                          value={value}
                          placeholder="Select "
                          styles={customStyles}
                          style={{width: '100%'}}
                          onChange={onChange}
                        >
                          {leaveTypes?.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                        </Select>
                      )}
                    />
                    <span className="text-danger">
                      {errors?.termination_type_id?.message}
                  </span>
                  </div>
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    From <span className="text-danger">*</span>
                  </label>
                  <div className="cal-icon">
                    <Controller
                      name="start_date"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <DatePicker
                          selected={value || null}
                          onChange={(value) => {
                            onChange(value);
                            setStartDate(value)
                          }}
                          className="form-control floating datetimepicker"
                          type="date"
                          dateFormat="dd-MM-yyyy"
                        />
                      )}
                    />
                    <span className="text-danger">
                      {errors?.start_date?.message}
                    </span>
                  </div>
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    To <span className="text-danger">*</span>
                  </label>
                  <div className="cal-icon">
                    <Controller
                      name="end_date"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <DatePicker
                          selected={value || null}
                          onChange={(value) => {
                            onChange(value);
                            setEndDate(value);
                          }}
                          className="form-control floating datetimepicker"
                          type="date"
                          dateFormat="dd-MM-yyyy"
                        />
                      )}
                    />
                    <span className="text-danger">
                      {errors?.end_date?.message}
                    </span>
                  </div>
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Number of days <span className="text-danger">*</span>
                  </label>
                  <input className="form-control" readOnly type="text" value={noOfDays} />
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Remaining Leaves <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    readOnly
                    defaultValue={12}
                    type="text"
                  />
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Leave Reason <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <textarea
                        className="form-control"
                        rows={4}
                        defaultValue={""}
                        onChange={onChange}
                      >{value}</textarea>
                    )}
                  />
                  <span className="text-danger">
                    {errors?.reason?.message}
                  </span>
                </div>
                <div className="submit-section">
                  <button
                    className="btn btn-primary submit-btn"
                    disabled={isLoading}
                    type="submit"
                  >
                    {!isLoading ? 'Submit' : <ClipLoader color="#ffffff" />}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
